import {
	AlertCircle,
	Bell,
	BellOff,
	Calendar,
	Download,
	FileText,
	Pencil,
	RefreshCw,
	Store,
	Trash2,
} from 'lucide-react'
import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'

import { CategoryBadge } from '@/components/category-badge'
import { LoadingButton } from '@/components/loading-button'
import { PageHeader } from '@/components/page-header'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import {
	AlertDialog,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import { WarrantyBadge } from '@/components/warranty-badge'
import { formatDate, formatPrice } from '@/lib/format'
import { trpc } from '@/trpc/client'

export function ItemDetailPage() {
	const { id = '' } = useParams<{ id: string }>()
	const navigate = useNavigate()
	const utils = trpc.useUtils()
	const [confirmOpen, setConfirmOpen] = useState(false)

	const itemQuery = trpc.items.get.useQuery({ id }, { enabled: id !== '' })
	const remove = trpc.items.delete.useMutation()

	async function handleDelete() {
		try {
			await remove.mutateAsync({ id })
			await utils.items.list.invalidate()
			toast.success('Item deleted')
			navigate('/app')
		} catch (err) {
			toast.error(
				err instanceof Error ? err.message : 'Could not delete this item',
			)
		}
	}

	if (itemQuery.isPending) {
		return (
			<div className="space-y-6">
				<Skeleton className="h-10 w-64" />
				<div className="grid gap-6 lg:grid-cols-[1fr_360px]">
					<Skeleton className="h-96 w-full" />
					<Skeleton className="h-96 w-full" />
				</div>
			</div>
		)
	}

	if (itemQuery.isError) {
		return (
			<Alert variant="destructive">
				<AlertCircle />
				<AlertTitle>Couldn't load this item</AlertTitle>
				<AlertDescription>
					{itemQuery.error.message}
					<div className="mt-2">
						<Button
							variant="outline"
							size="sm"
							onClick={() => itemQuery.refetch()}
						>
							<RefreshCw className="size-4" />
							Retry
						</Button>
					</div>
				</AlertDescription>
			</Alert>
		)
	}

	const item = itemQuery.data

	return (
		<div className="space-y-8">
			<PageHeader
				title={item.name}
				description={item.store ?? undefined}
				actions={
					<>
						<Button asChild variant="outline">
							<Link to={`/app/items/${item.id}/edit`}>
								<Pencil className="size-4" />
								Edit
							</Link>
						</Button>
						<AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
							<AlertDialogTrigger asChild>
								<Button variant="outline" className="text-destructive">
									<Trash2 className="size-4" />
									Delete
								</Button>
							</AlertDialogTrigger>
							<AlertDialogContent>
								<AlertDialogHeader>
									<AlertDialogTitle>Delete this item?</AlertDialogTitle>
									<AlertDialogDescription>
										This will permanently remove “{item.name}” and its uploaded
										file. This can't be undone.
									</AlertDialogDescription>
								</AlertDialogHeader>
								<AlertDialogFooter>
									<AlertDialogCancel disabled={remove.isPending}>
										Cancel
									</AlertDialogCancel>
									<LoadingButton
										variant="destructive"
										pending={remove.isPending}
										onClick={handleDelete}
									>
										<Trash2 className="size-4" />
										Delete item
									</LoadingButton>
								</AlertDialogFooter>
							</AlertDialogContent>
						</AlertDialog>
					</>
				}
			/>

			<div className="grid gap-6 lg:grid-cols-[1fr_360px]">
				<FilePreview
					fileUrl={item.fileUrl}
					mimeType={item.fileMimeType}
					originalName={item.fileOriginalName}
				/>

				<Card className="h-fit gap-0 py-0">
					<CardContent className="p-6 space-y-5">
						<div>
							<p className="text-xs uppercase tracking-wide text-muted-foreground">
								Price
							</p>
							<p className="mt-1 text-2xl font-semibold tabular-nums tracking-tight">
								{formatPrice(item.price, item.currency)}
							</p>
						</div>

						<Separator />

						<DetailRow
							icon={Calendar}
							label="Purchase date"
							value={formatDate(item.purchaseDate)}
						/>

						{item.store ? (
							<DetailRow icon={Store} label="Store" value={item.store} />
						) : null}

						<div>
							<p className="text-xs text-muted-foreground">Warranty</p>
							<div className="mt-2">
								<WarrantyBadge expiresAt={item.warrantyExpiresAt} />
							</div>
							{item.warrantyExpiresAt ? (
								<p className="mt-2 text-xs tabular-nums text-muted-foreground">
									Expires {formatDate(item.warrantyExpiresAt)}
								</p>
							) : null}
						</div>

						<DetailRow
							icon={item.notifyBeforeExpiry ? Bell : BellOff}
							label="Notification"
							value={
								item.notifyBeforeExpiry
									? `${item.notifyDaysBefore} days before expiry`
									: 'Off'
							}
						/>

						{item.categories.length > 0 ? (
							<>
								<Separator />
								<div>
									<p className="text-xs text-muted-foreground">Categories</p>
									<div className="mt-2 flex flex-wrap gap-1.5">
										{item.categories.map((cat) => (
											<CategoryBadge
												key={cat.id}
												name={cat.name}
												color={cat.color}
											/>
										))}
									</div>
								</div>
							</>
						) : null}

						{item.notes ? (
							<>
								<Separator />
								<div>
									<p className="text-xs text-muted-foreground">Notes</p>
									<p className="mt-2 whitespace-pre-wrap text-sm">
										{item.notes}
									</p>
								</div>
							</>
						) : null}
					</CardContent>
				</Card>
			</div>
		</div>
	)
}

function DetailRow({
	icon: Icon,
	label,
	value,
}: {
	icon: typeof Calendar
	label: string
	value: string
}) {
	return (
		<div className="flex items-start gap-3">
			<Icon className="mt-0.5 size-4 text-muted-foreground" />
			<div>
				<p className="text-xs text-muted-foreground">{label}</p>
				<p className="text-sm tabular-nums">{value}</p>
			</div>
		</div>
	)
}

function FilePreview({
	fileUrl,
	mimeType,
	originalName,
}: {
	fileUrl: string | null
	mimeType: string | null
	originalName: string | null
}) {
	if (!fileUrl) {
		return (
			<Card className="flex min-h-[400px] items-center justify-center bg-muted/30 py-0">
				<div className="text-center text-muted-foreground">
					<FileText className="mx-auto size-10 opacity-50" />
					<p className="mt-3 text-sm">No file attached</p>
				</div>
			</Card>
		)
	}

	const isImage = mimeType?.startsWith('image/')
	const isPdf = mimeType === 'application/pdf'

	return (
		<Card className="overflow-hidden py-0 gap-0">
			{isImage ? (
				<a
					href={fileUrl}
					target="_blank"
					rel="noreferrer noopener"
					className="block bg-muted/30"
				>
					<img
						src={fileUrl}
						alt={originalName ?? 'Receipt'}
						className="max-h-[600px] w-full object-contain"
					/>
				</a>
			) : isPdf ? (
				<iframe
					title={originalName ?? 'PDF preview'}
					src={fileUrl}
					className="h-[600px] w-full"
				/>
			) : (
				<div className="flex h-[400px] items-center justify-center bg-muted/30">
					<div className="text-center text-muted-foreground">
						<FileText className="mx-auto size-10 opacity-50" />
						<p className="mt-3 text-sm">Preview not available</p>
					</div>
				</div>
			)}
			<div className="flex items-center justify-between gap-3 border-t bg-card px-4 py-3 text-sm">
				<span className="truncate text-muted-foreground">
					{originalName ?? 'Attached file'}
				</span>
				<Button asChild variant="outline" size="sm">
					<a href={fileUrl} target="_blank" rel="noreferrer noopener">
						<Download className="size-4" />
						Download
					</a>
				</Button>
			</div>
		</Card>
	)
}
